import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ExternalLink, MapPin } from "lucide-react";

import { useGoalProfile, useGoals, useMap, useProfile } from "@/lib/queries";

/**
 * The ICP, in one place — who they are, and where to find them.
 *
 * Two halves, and the second is the point. A buyer description is a paragraph
 * a founder can already recite; what she cannot do from memory is name the
 * rooms those people are actually in this month. So the top of this page is a
 * summary of what she told Omina, and the bottom is what Omina did with it.
 *
 * Everything here is read-only. The answers live in two places already — the
 * sentence on `profiles`, the detail on `customer_profiles` — and this page
 * composes them rather than storing a third copy. Editing goes back to the
 * conversation that collected them, which is the only screen that writes.
 *
 * **Where an answer is missing it says so and offers the question**, rather
 * than hiding the row. A half-filled ICP that looks complete is how a founder
 * concludes Omina knows her buyer when it does not.
 */
export const Route = createFileRoute("/icp-profile")({
  component: IcpProfile,
});

function IcpProfile() {
  const profile = useProfile();
  const goals = useGoals();
  const custGoal = goals.data?.goals.find((g) => g.type === "cust");
  const goalProfile = useGoalProfile(custGoal?.id);
  const map = useMap("cust");

  const p = profile.data;
  /**
   * The deeper answers, from tier two of the blueprint. `profile` on the goal
   * row is `customer_profiles`; it is null until she has answered them.
   */
  const deep = goalProfile.data?.profile as
    | {
        companySizeRange?: string | null;
        dreamCustomers?: string[] | null;
        acquisitionChannels?: string[] | null;
        targetGeos?: string[] | null;
        painPoints?: string | null;
      }
    | undefined;

  const mapPayload = map.data?.map?.payload;
  const categories = mapPayload?.categories ?? [];
  const rooms = categories.flatMap((c) => c.items ?? []);

  return (
    <div className="icppwrap">
      <header className="icpphead">
        <div className="eyebrow">Your ICP</div>
        <h1 className="tk tk-want">Who buys, and where they are</h1>
      </header>

      {/* ── who ───────────────────────────────────────────────── */}
      <section className="icppcard">
        {p?.buyerRole || p?.buyerCompany ? (
          // Her own sentence, not a field list. It is the shape the blueprint
          // collected it in, and reads back as something she said.
          <p className="icppsentence">
            I sell to <b>{p.buyerRole || "…"}</b>
            {p.buyerCompany && (
              <>
                {" "}
                at <b>{p.buyerCompany}</b>
              </>
            )}
            .
            {p.buyerPain && (
              <>
                {" "}
                They come to me because <b>{p.buyerPain}</b>.
              </>
            )}
          </p>
        ) : (
          <p className="lede">
            Omina does not know who buys yet. Five questions is all it takes.
          </p>
        )}

        <ul className="icppfacts">
          <Fact label="Industry" value={p?.targetIndustry} />
          <Fact
            label="Buyer"
            value={p?.audience === "b2b" ? "A business" : p?.audience === "b2c" ? "A person" : null}
          />
          <Fact label="Company size" value={deep?.companySizeRange} />
          <Fact label="Where they are" value={deep?.targetGeos?.join(", ")} />
          <Fact label="Dream customers" value={deep?.dreamCustomers?.join(", ")} />
          <Fact label="Best channel so far" value={deep?.acquisitionChannels?.join(", ")} />
          <Fact label="What they try first" value={deep?.painPoints} />
        </ul>

        <Link to="/icp" className="btn ghost icppedit">
          {p?.buyerRole ? "Sharpen this" : "Answer the questions"}
          <ArrowRight size={15} aria-hidden />
        </Link>
      </section>

      {/* ── where ─────────────────────────────────────────────── */}
      <h2 className="icppsub">Where to find them</h2>

      {map.isPending ? (
        <p className="hint">Looking…</p>
      ) : rooms.length === 0 ? (
        /**
         * The empty map, said plainly — rule #2. The curated base only covers
         * some industries, and padding this with another industry's rooms is
         * the exact failure that rule exists to prevent. So it says the base
         * does not reach her yet and points at the radar, which is where real
         * rooms get verified into it.
         */
        <div className="icppempty">
          <p className="lede">
            No verified rooms for {p?.targetIndustry || "your industry"} yet. Omina
            would rather show you nothing than another industry's rooms.
          </p>
          <Link to="/feed" className="btn ghost">
            Find rooms on the radar
          </Link>
        </div>
      ) : (
        <ul className="icpprooms">
          {rooms.slice(0, 8).map((r, i) => (
            <li key={`${r.name}-${i}`}>
              <article className="icpproom">
                <div className="icpproomtop">
                  <h3>{r.name}</h3>
                  {/* Verified or not, on its face. Rule #1: the founder can
                      always see which of these a human vouched for. */}
                  {r.status && (
                    <span className={`icppstatus ${r.status}`}>{r.status}</span>
                  )}
                </div>
                {r.why && <p className="icppwhy">{r.why}</p>}
                <p className="icppmeta">
                  {r.location && (
                    <span>
                      <MapPin size={11} aria-hidden />
                      {r.location}
                    </span>
                  )}
                  {/* `source` is where the entry came from, and it is not
                      always a link — for a hand-verified room it is often the
                      name of the person or list it came off. Rendering a
                      non-URL as an anchor gives her a dead click, so it only
                      becomes one when it actually parses. */}
                  {r.source &&
                    (isUrl(r.source) ? (
                      <a href={r.source} target="_blank" rel="noreferrer noopener">
                        Open
                        <ExternalLink size={11} aria-hidden />
                      </a>
                    ) : (
                      <span>{r.source}</span>
                    ))}
                </p>
              </article>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function isUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

/**
 * One fact. Missing values render as a prompt rather than disappearing —
 * an absent row is indistinguishable from a fact Omina simply has, and this
 * page's whole job is showing her which is which.
 */
function Fact({ label, value }: { label: string; value?: string | null }) {
  return (
    <li className={value ? undefined : "isempty"}>
      <span className="icppfactlabel">{label}</span>
      <span className="icppfactvalue">{value || "Not answered yet"}</span>
    </li>
  );
}
